(() => {
  if (customElements.get('sticky-kaching-atc')) return;

  class StickyKachingATC extends HTMLElement {
    connectedCallback() {
      // prevent double init if element is reconnected
      if (this._isMounted) return;
      this._isMounted = true;

      // --- UI refs (single + multi) ---
      this.$btn = this.querySelector('[data-sk-btn]');
      this.$btnText = this.querySelector('.sk-btn__text');

      // --- selectors ---
      this.formSel = 'form[data-type="add-to-cart-form"]';
      this.barRootSel = '.kaching-bundles__bar';
      this.selectedBarClass = 'kaching-bundles__bar--selected';

      // --- sticky visibility state ---
      this._atcBtn = null;
      this._seenAtc = false;
      this._atcOffsetTop = null;
      this._atcHeight = null;

      // --- timers/observers ---
      this._retryT = null;
      this._loadingT = null;
      this._hydrateRAF = null;
      this._io = null;
      this._mo = null;
      this._pollIV = null;

      // --- bound handlers (so we can remove safely) ---
      this._onStickyBtnClick = this.onStickyBtnClick.bind(this);
      this._onDocClick = this.onDocumentClick.bind(this);
      this._onDocChange = this.onDocumentChange.bind(this);
      this._onScrollBound = null;
      this._onResizeBound = null;

      // --- initial hydrate (Kaching renders async) ---
      this.hydrate();
      this.startHydratePolling(); // stops automatically once bars are present

      // --- sticky button click ---
      this.$btn?.addEventListener('click', this._onStickyBtnClick);

      // --- visibility observer for sticky show/hide ---
      this.initAtcObserver();

      // --- always keep prices in sync with active bundle/subscription ---
      this.bindLiveListeners();
      this.initPriceObservers();
    }

    disconnectedCallback() {
      if (this._io) this._io.disconnect();
      if (this._mo) this._mo.disconnect();

      if (this._retryT) clearTimeout(this._retryT);
      if (this._loadingT) clearTimeout(this._loadingT);
      if (this._hydrateRAF) cancelAnimationFrame(this._hydrateRAF);
      if (this._pollIV) clearInterval(this._pollIV);

      if (this.$btn && this._onStickyBtnClick) {
        this.$btn.removeEventListener('click', this._onStickyBtnClick);
      }

      if (this._boundLive) {
        document.removeEventListener('click', this._onDocClick);
        document.removeEventListener('change', this._onDocChange);
        this._boundLive = false;
      }

      if (this._onScrollBound) {
        window.removeEventListener('scroll', this._onScrollBound);
        this._onScrollBound = null;
      }
      if (this._onResizeBound) {
        window.removeEventListener('resize', this._onResizeBound);
        this._onResizeBound = null;
      }

      this._isMounted = false;
    }

    // -------------------------
    // UI getters / sync helpers
    // -------------------------
    getSaleNodes() {
      // IMPORTANT: re-query every time (mobile/desktop nodes may be duplicated
      // or rerendered asynchronously)
      return Array.from(this.querySelectorAll('[data-sk-sale]'));
    }

    setSaleText(text) {
      const nodes = this.getSaleNodes();
      if (!nodes.length) return;
      nodes.forEach((el) => {
        el.textContent = text;
      });
    }

    // -------------------------
    // Sticky loading / UI
    // -------------------------
    onStickyBtnClick(e) {
      e.preventDefault();
      this.setLoading(true);
      this.clickRealAddToCart();
    }

    setLoading(isLoading) {
      if (!this.$btn) return;

      this.$btn.classList.toggle('is-loading', !!isLoading);

      if (this.$btnText) {
        this.$btnText.textContent = isLoading ? 'Adding…' : 'Add to cart';
      }

      if (this._loadingT) clearTimeout(this._loadingT);

      // safety fallback
      if (isLoading) {
        this._loadingT = setTimeout(() => this.setLoading(false), 3000);
      }
    }

    showSticky() {
      this.classList.add('sk-sticky--shown');
      this.classList.remove('sk-sticky--hidden');
    }

    hideSticky() {
      this.classList.add('sk-sticky--hidden');
      this.classList.remove('sk-sticky--shown');
      this.setLoading(false);
    }

    // -------------------------
    // Sticky show/hide logic
    // -------------------------
    computeAtcMetrics() {
      if (!this._atcBtn) return;

      const rect = this._atcBtn.getBoundingClientRect();
      const y = window.scrollY || document.documentElement.scrollTop || 0;

      this._atcOffsetTop = rect.top + y;
      this._atcHeight = rect.height || this._atcHeight || 0;
    }

    updateStickyVisibility() {
      if (!this._atcBtn || !document.contains(this._atcBtn)) {
        // ATC button may be re-rendered by Shopify/app; try to rebind once
        const nextBtn = this.getRealAtcButton();
        if (nextBtn && nextBtn !== this._atcBtn) {
          this._atcBtn = nextBtn;
          this.computeAtcMetrics();

          if (this._io) this._io.disconnect();
          this._io = new IntersectionObserver(() => this.updateStickyVisibility(), {
            root: null,
            threshold: 0
          });
          this._io.observe(nextBtn);
        } else {
          this.hideSticky();
          return;
        }
      }

      const rect = this._atcBtn.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight || 0;

      const atcVisible = rect.bottom > 0 && rect.top < vh;
      const atcAboveViewport = rect.bottom < 0;

      if (atcVisible) this._seenAtc = true;

      if (atcVisible) {
        this.hideSticky();
        return;
      }

      const y = window.scrollY || document.documentElement.scrollTop || 0;
      const atcTop = this._atcOffsetTop ?? (rect.top + y);
      const atcH = this._atcHeight ?? rect.height ?? 0;

      const isBelowAtc = y > (atcTop + atcH);
      const allow = this._seenAtc || isBelowAtc;

      if (allow && atcAboveViewport) this.showSticky();
      else this.hideSticky();
    }

    initAtcObserver() {
      const setup = () => {
        const atcBtn = this.getRealAtcButton();
        if (!atcBtn) return false;

        this._atcBtn = atcBtn;
        this.computeAtcMetrics();

        if (this._io) this._io.disconnect();

        this._io = new IntersectionObserver(
          () => this.updateStickyVisibility(),
          { root: null, threshold: 0 }
        );

        this._io.observe(atcBtn);

        if (this._onScrollBound) {
          window.removeEventListener('scroll', this._onScrollBound);
        }
        this._onScrollBound = () => this.updateStickyVisibility();
        window.addEventListener('scroll', this._onScrollBound, { passive: true });

        if (this._onResizeBound) {
          window.removeEventListener('resize', this._onResizeBound);
        }
        this._onResizeBound = () => {
          this.computeAtcMetrics();
          this.updateStickyVisibility();
        };
        window.addEventListener('resize', this._onResizeBound, { passive: true });

        this.hideSticky();
        this.updateStickyVisibility();

        return true;
      };

      const trySetup = () => {
        const ok = setup();
        if (!ok) this._retryT = setTimeout(trySetup, 250);
      };

      trySetup();
    }

    // -------------------------
    // Kaching: active bundle
    // -------------------------
    getBars() {
      return document.querySelectorAll(this.barRootSel);
    }

    getActiveBar() {
      const selected = document.querySelector(
        `${this.barRootSel}.${this.selectedBarClass}`
      );
      if (selected) return selected;

      const first = document.querySelector(this.barRootSel);
      return first || null;
    }

    // -------------------------
    // Money parsing helpers
    // -------------------------
    moneyFromEl(el) {
      if (!el) return null;

      const c = el.cloneNode(true);

      // remove unit label like "/each"
      const unit = c.querySelector('.kaching-bundles__bar-price__unit-label');
      if (unit) unit.remove();

      const raw = (c.textContent || '').replace(/\s+/g, ' ').trim();
      if (!raw) return null;

      const m = raw.match(/-?[\d.,]+/);
      if (!m) return null;

      const numStr = m[0];
      const idx = raw.indexOf(numStr);
      const prefix = raw.slice(0, idx).trim();
      const suffix = raw.slice(idx + numStr.length).trim();

      const lastDot = numStr.lastIndexOf('.');
      const lastComma = numStr.lastIndexOf(',');
      const decPos = Math.max(lastDot, lastComma);

      let normalized = '';
      if (decPos >= 0) {
        const intPart = numStr.slice(0, decPos).replace(/[.,]/g, '');
        const fracPart = numStr.slice(decPos + 1).replace(/[.,]/g, '');
        normalized = intPart + '.' + fracPart;
      } else {
        normalized = numStr.replace(/[.,]/g, '');
      }

      const n = Number(normalized);
      if (!Number.isFinite(n)) return null;

      return { n, prefix, suffix };
    }

    formatMoney(m, value) {
      const safeVal = Number.isFinite(value) ? value : 0;
      const v = safeVal.toFixed(2);
      return `${m.prefix ? m.prefix : ''}${v}${m.suffix ? ' ' + m.suffix : ''}`.trim();
    }

    getBundleQty() {
      const form = document.querySelector(this.formSel);
      if (!form) return 1;

      // 1) standard Shopify quantity
      const qInput = form.querySelector('input[name="quantity"]');
      const q1 = qInput ? parseInt(qInput.value, 10) : NaN;
      if (Number.isFinite(q1) && q1 > 0) return q1;

      // 2) hidden inputs with qty/quantity/count
      const hidden = form.querySelectorAll('input[type="hidden"]');
      for (const inp of hidden) {
        const name = (inp.getAttribute('name') || '').toLowerCase();
        const id = (inp.getAttribute('id') || '').toLowerCase();

        if (
          name.includes('qty') || name.includes('quantity') || name.includes('count') ||
          id.includes('qty') || id.includes('quantity') || id.includes('count')
        ) {
          const n = parseInt(inp.value, 10);
          if (Number.isFinite(n) && n > 0) return n;
        }
      }

      // 3) properties[...] with qty
      for (const inp of hidden) {
        const name = (inp.getAttribute('name') || '').toLowerCase();
        if (
          name.startsWith('properties[') &&
          (name.includes('qty') || name.includes('quantity') || name.includes('count'))
        ) {
          const n = parseInt(inp.value, 10);
          if (Number.isFinite(n) && n > 0) return n;
        }
      }

      return 1;
    }

    hydrate() {
      const bar = this.getActiveBar();
      if (!bar) return;

      const saleEl = bar.querySelector('.kaching-bundles__bar-price');
      const regEl = bar.querySelector('.kaching-bundles__bar-full-price');

      const s = this.moneyFromEl(saleEl);
      const r = this.moneyFromEl(regEl); // keep parsed if later you'll need compare/save

      const qty = this.getBundleQty();

      // Sync ALL [data-sk-sale] nodes (desktop + mobile)
      if (s) {
        this.setSaleText(this.formatMoney(s, s.n * qty));
      }

      // keep these lines so linter doesn't strip if you expand later
      void r;
      const label = bar.querySelector('.kaching-bundles__bar-label')?.textContent || '';
      void label;
    }

    scheduleHydrate() {
      if (this._hydrateRAF) cancelAnimationFrame(this._hydrateRAF);
      this._hydrateRAF = requestAnimationFrame(() => this.hydrate());
    }

    startHydratePolling() {
      if (this._pollIV) clearInterval(this._pollIV);

      let tries = 0;
      this._pollIV = setInterval(() => {
        this.scheduleHydrate();
        tries += 1;

        // stop once bars exist or after ~7.5s
        if (tries >= 30 || this.getBars().length) {
          clearInterval(this._pollIV);
          this._pollIV = null;
        }
      }, 250);
    }

    // -------------------------
    // Keep in sync with clicks/changes + async rerenders
    // -------------------------
    onDocumentClick(e) {
      if (e.target?.closest?.(this.barRootSel)) {
        this.scheduleHydrate();

        // Kaching may toggle selected class async
        setTimeout(() => this.scheduleHydrate(), 0);
        setTimeout(() => this.scheduleHydrate(), 80);
      }
    }

    onDocumentChange(e) {
      const t = e.target;
      if (!t || !t.matches) return;

      if (t.matches(`${this.barRootSel} input[type="radio"]`)) {
        this.scheduleHydrate();
        setTimeout(() => this.scheduleHydrate(), 50);
        return;
      }

      // Loop / selling plans (broad selectors)
      if (
        t.matches('input[type="radio"][name*="selling_plan"]') ||
        t.matches('select[name*="selling_plan"]') ||
        t.closest('[class*="loop"]') ||
        t.closest('[data-loop]')
      ) {
        this.scheduleHydrate();
        setTimeout(() => this.scheduleHydrate(), 50);
        setTimeout(() => this.scheduleHydrate(), 150);
      }

      // quantity changes in form
      if (
        t.matches(`${this.formSel} input[name="quantity"]`) ||
        t.matches(`${this.formSel} select[name="quantity"]`)
      ) {
        this.scheduleHydrate();
      }
    }

    bindLiveListeners() {
      if (this._boundLive) return;
      this._boundLive = true;

      document.addEventListener('click', this._onDocClick);
      document.addEventListener('change', this._onDocChange);
    }

    initPriceObservers() {
      if (this._mo) this._mo.disconnect();

      const roots = [
        document.querySelector('.kaching-bundles'),
        document.querySelector(this.formSel),
        document.querySelector('main'),
      ].filter(Boolean);

      if (!roots.length) return;

      this._mo = new MutationObserver((mutations) => {
        for (const m of mutations) {
          if (
            m.type === 'childList' ||
            m.type === 'characterData' ||
            (m.type === 'attributes' && m.attributeName === 'class') ||
            (m.type === 'attributes' && m.attributeName === 'value')
          ) {
            this.scheduleHydrate();
            break;
          }
        }
      });

      roots.forEach((r) => {
        this._mo.observe(r, {
          subtree: true,
          childList: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['class', 'value'],
        });
      });
    }

    // -------------------------
    // Real ATC click forwarding
    // -------------------------
    getRealAtcButton() {
      return (
        document.querySelector(`${this.formSel} button[type="submit"][name="add"]`) ||
        document.querySelector(`${this.formSel} button[name="add"]`) ||
        document.querySelector(`${this.formSel} button[type="submit"]`) ||
        document.querySelector(`${this.formSel} input[type="submit"]`) ||
        null
      );
    }

    ensureActiveBundleSelected() {
      // We want the current active bar (selected or first)
      const bar = this.getActiveBar();
      if (!bar) return;

      // If Kaching uses radio, trigger it so their state is correct
      const radio = bar.querySelector('input[type="radio"]');
      if (radio && !radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('input', { bubbles: true }));
        radio.dispatchEvent(new Event('change', { bubbles: true }));
        // click can re-toggle in some widgets; trigger only if still unchecked after change
        if (!radio.checked) radio.click();
      }
    }

    clickRealAddToCart() {
      // Ensure Kaching bundle selection matches what sticky shows
      this.ensureActiveBundleSelected();

      const realBtn = this.getRealAtcButton();
      if (!realBtn) {
        this.setLoading(false);
        return;
      }

      setTimeout(() => {
        realBtn.click();
        setTimeout(() => this.setLoading(false), 900);
      }, 120);
    }
  }

  customElements.define('sticky-kaching-atc', StickyKachingATC);
})();